import { useState } from 'react';
import { X, AlertTriangle, Info } from 'lucide-react';
import { useRoosterStore } from '../store/useRoosterStore.js';
import { bezettingPersonen } from '../lib/dienstBezetting.js';
import { maakSleutel } from '../lib/sleutels.js';
import {
  CEL, PALET, GERESERVEERDE_CODES, normaliseerCelCode, paletSleutelVoorKleur,
  celCodeVoorDienst, gebruikteCelCodes, suggestieCelCode, suggestieCelKleur,
} from '../lib/roostercodes.js';

const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const DAG_LABELS = { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr', za: 'Za', zo: 'Zo' };

function DagToggle({ waarde = [], onChange }) {
  return (
    <div className="flex gap-1.5">
      {DAGEN.map(d => (
        <button
          key={d}
          type="button"
          onClick={() => {
            const nieuw = waarde.includes(d) ? waarde.filter(x => x !== d) : [...waarde, d];
            onChange(nieuw);
          }}
          className={`w-10 h-8 rounded text-xs font-medium transition ${
            waarde.includes(d) ? 'bg-brand-700 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
        >
          {DAG_LABELS[d]}
        </button>
      ))}
    </div>
  );
}

// Levert { fouten, waarschuwing }. Alleen `fouten` blokkeert opslaan; de waarschuwing
// is puur informatief en loopt bewust NIET mee in de disabled-knop of de early return.
function valideer(form, { regels, gemKey }) {
  const fouten = [];
  let waarschuwing = null;
  if (!form.label?.trim()) fouten.push('Geef een naam op voor de dienst.');
  if (!(form.week_a_dagen || form.week_b_dagen) && !form.dagen?.length) fouten.push('Selecteer minstens één dag.');
  if ((form.min_personen || 0) < 1) fouten.push('Er moet minimaal 1 persoon nodig zijn.');
  if ((form.max_personen || 0) < (form.min_personen || 0))
    fouten.push('Het maximum moet ≥ het minimum zijn.');

  // ── Cel-code: twee weigeringen + één waarschuwing ───────────────────────
  const code = normaliseerCelCode(form.cel_code);
  if (!code) {
    fouten.push('Geef een cel-code op: 1 tot 3 letters of cijfers.');
  } else if (GERESERVEERDE_CODES.includes(code)) {
    // Deze letters hebben in de matrix al een andere betekenis; een dienst met
    // code 'S' zou meetellen in de School-rij van de dagweergave.
    fouten.push(`Code '${code}' is gereserveerd voor ${CEL[code]?.label || code}. Kies een andere code.`);
  } else {
    // Alleen kijken naar ANDERE gemeenten. Binnen één gemeente is een gedeelde code
    // de normale toestand — Arnhem heeft 2x L, Lingewaard 2x LW — en de dag-weergave
    // rekent daarop: één code = één rij (daglijstGroep in roostercodes.js). Daar dus geen woord over.
    const anderen = (gebruikteCelCodes(regels).get(code) || []).filter(h => h.gemKey !== gemKey);
    if (anderen.length > 0) {
      const h = anderen[0];
      // Geen weigering: dit mag, maar je moet het wel wéten. In de matrix vallen de
      // twee diensten samen tot dezelfde cel en zijn ze niet meer uit elkaar te houden.
      waarschuwing =
        `Code '${code}' is ook van ${h.gemNaam} — ${h.dienstLabel}. In de matrix zijn die twee dan niet uit elkaar te houden. Opslaan kan gewoon.`;
    }
  }
  return { fouten, waarschuwing };
}

export default function DienstModal({ gemKey, dienstKey, onSluit }) {
  const { regels, updateDienst, addDienst } = useRoosterStore();
  const isNieuw = !dienstKey;
  const gemeente = regels.gemeenten[gemKey];
  const bestaand = dienstKey ? gemeente?.diensten?.[dienstKey] : null;
  // Cyclusdienst = bi-wekelijkse rotatie (week_a_dagen/week_b_dagen) i.p.v. een vaste dagen-lijst.
  // Gated op de data-velden, dus Arnhem/Rheden én de Lingewaard-dagdienst (gewone `dagen`) vallen erbuiten.
  const isCyclus = !!(bestaand?.week_a_dagen || bestaand?.week_b_dagen);

  // Voorvulling van code+kleur. Bij een BESTAANDE dienst zonder cel_code tonen we
  // wat de terugval nu al oplevert (code én bijbehorende paletkleur), zodat opslaan
  // zonder aanpassing niets verandert aan wat je op het scherm ziet. Bij een nieuwe
  // dienst een vrije suggestie uit de gemeentenaam + een nog ongebruikte kleur.
  const huidigeCel = dienstKey ? celCodeVoorDienst(regels, gemKey, dienstKey) : null;
  const initCode = bestaand
    ? (normaliseerCelCode(bestaand.cel_code) || huidigeCel?.code || '')
    : suggestieCelCode(regels, gemKey);
  const initKleur = bestaand
    ? (PALET[bestaand.cel_kleur] ? bestaand.cel_kleur : (paletSleutelVoorKleur(huidigeCel?.bg) || 'grijs'))
    : suggestieCelKleur(regels);

  // Bezetting wordt in PERSONEN ingesteld. De beginwaarden zijn wat de engine vandaag
  // al afdwingt voor deze dienst (min_personen als die er staat, anders koppels x grootte),
  // zodat openen-en-opslaan zonder typen niets verschuift.
  const kg = regels.instellingen?.koppel_grootte || 2;
  const startBezetting = bestaand ? bezettingPersonen(bestaand, kg) : { min: 2, max: 4 };

  const [form, setForm] = useState(bestaand
    ? {
        label:       bestaand.label || dienstKey,
        flexibel:    bestaand.tijd_start === 'flexibel',
        tijd_start:  bestaand.tijd_start === 'flexibel' ? '14:00' : (bestaand.tijd_start || '14:00'),
        tijd_eind:   bestaand.tijd_eind  === 'flexibel' ? '22:00' : (bestaand.tijd_eind  || '22:00'),
        min_personen: startBezetting.min,
        max_personen: startBezetting.max,
        dagen:       bestaand.dagen || [],
        week_a_dagen: bestaand.week_a_dagen,
        week_b_dagen: bestaand.week_b_dagen,
        cel_code:    initCode,
        cel_kleur:   initKleur,
      }
    : {
        label: '', flexibel: false,
        tijd_start: '14:00', tijd_eind: '22:00',
        min_personen: startBezetting.min, max_personen: startBezetting.max,
        dagen: ['ma', 'di', 'wo', 'do', 'vr'],
        cel_code:    initCode,
        cel_kleur:   initKleur,
      }
  );

  const { fouten, waarschuwing } = valideer(form, { regels, gemKey });

  function slaOp() {
    if (fouten.length > 0) return;

    const minP = Number(form.min_personen);
    const maxP = Number(form.max_personen);

    const data = {
      label:       form.label.trim(),
      tijd_start:  form.flexibel ? 'flexibel' : form.tijd_start,
      tijd_eind:   form.flexibel ? 'flexibel' : form.tijd_eind,
      min_personen: minP,
      max_personen: maxP,
      // min_koppels/max_koppels liften mee, ze worden hier niet meer ingesteld. Ze zijn nog
      // steeds de TERUGVAL in berekenBezettingsDoel (rooster-engine.js) voor diensten
      // zonder min_personen — deze dienst krijgt dat veld wel, dus voor hem is de terugval
      // niet nodig. Toch bijhouden, zodat de data zelf-consistent blijft: elke dienst
      // beschrijft hetzelfde doel in beide eenheden en een back-up of een lezer die alleen
      // de koppel-velden kent, ziet niet iets anders dan de engine afdwingt.
      min_koppels: Math.ceil(minP / kg),
      max_koppels: Math.ceil(maxP / kg),
      cel_code:    normaliseerCelCode(form.cel_code),
      cel_kleur:   PALET[form.cel_kleur] ? form.cel_kleur : 'grijs',
      // Cyclusdienst: behoud de week_a/b-rotatie (en geen overbodige lege `dagen`); anders de gewone dagen-lijst.
      ...(isCyclus
        ? { week_a_dagen: form.week_a_dagen, week_b_dagen: form.week_b_dagen }
        : { dagen: form.dagen }),
    };

    if (isNieuw) {
      const key = maakSleutel(form.label);
      addDienst(gemKey, key, data);
    } else {
      updateDienst(gemKey, dienstKey, data);
    }
    onSluit();
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">
            {isNieuw ? `Nieuwe dienst — ${gemeente?.naam}` : `Bewerk: ${bestaand?.label || dienstKey}`}
          </h2>
          <button onClick={onSluit} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Naam */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Naam dienst</label>
            <input
              type="text"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="bijv. Late dienst"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* Cel-code + kleur — hoe deze dienst in de rooster-matrix verschijnt */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cel in de matrix</label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={form.cel_code}
                onChange={e => setForm(f => ({ ...f, cel_code: normaliseerCelCode(e.target.value) }))}
                maxLength={3}
                placeholder="bijv. MA"
                className="w-20 border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold text-center uppercase focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <span
                className="inline-block rounded font-bold text-center leading-tight"
                style={{
                  width: 34, padding: '3px 0', fontSize: 11,
                  background: (PALET[form.cel_kleur] || PALET.grijs).bg,
                  color: (PALET[form.cel_kleur] || PALET.grijs).fg,
                }}
              >
                {form.cel_code || '?'}
              </span>
              <span className="text-xs text-slate-400">1–3 tekens; zo staat de dienst straks in de matrix.</span>
            </div>

            <div className="flex flex-wrap gap-1.5 mt-2">
              {Object.entries(PALET).map(([sleutel, kleur]) => (
                <button
                  key={sleutel}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, cel_kleur: sleutel }))}
                  title={kleur.naam}
                  className={`inline-flex items-center justify-center rounded font-bold transition ${
                    form.cel_kleur === sleutel ? 'ring-2 ring-brand-600 ring-offset-1' : 'hover:opacity-80'
                  }`}
                  style={{ width: 34, height: 24, fontSize: 11, background: kleur.bg, color: kleur.fg }}
                >
                  {form.cel_code || '?'}
                </button>
              ))}
            </div>
          </div>

          {/* Bezetting — in personen, niet in koppels */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Minimaal aantal personen</label>
              <input
                type="number" min="1" max="20"
                value={form.min_personen}
                onChange={e => setForm(f => ({
                  ...f,
                  min_personen: Number(e.target.value),
                  max_personen: Math.max(f.max_personen, Number(e.target.value)),
                }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Maximaal aantal personen</label>
              <input
                type="number" min={form.min_personen} max="20"
                value={form.max_personen}
                onChange={e => setForm(f => ({ ...f, max_personen: Number(e.target.value) }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>

          {/* Dagen */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Actieve dagen</label>
            {isCyclus ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                <div className="text-sm text-slate-700">
                  Vaste 2-weken-cyclus —{' '}
                  {form.week_a_dagen?.length > 0 && (
                    <span>Week A: {form.week_a_dagen.map(d => DAG_LABELS[d]).join(', ')}</span>
                  )}
                  {form.week_a_dagen?.length > 0 && form.week_b_dagen?.length > 0 && ' · '}
                  {form.week_b_dagen?.length > 0 && (
                    <span>Week B: {form.week_b_dagen.map(d => DAG_LABELS[d]).join(', ')}</span>
                  )}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Vaste rotatie — beheerd via de instellingen (code), niet hier bewerkbaar.
                </div>
              </div>
            ) : (
              <DagToggle waarde={form.dagen} onChange={v => setForm(f => ({ ...f, dagen: v }))} />
            )}
          </div>

          {/* Validatiefouten — amber, blokkeren opslaan */}
          {fouten.length > 0 && (
            <div className="space-y-1">
              {fouten.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  {f}
                </div>
              ))}
            </div>
          )}

          {/* Waarschuwing — neutraal grijs, bewust ANDERS dan de amber fouten:
              dit blokkeert niets, de knop blijft gewoon werken. */}
          {waarschuwing && (
            <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
              <span>{waarschuwing}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onSluit} className="flex-1 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition">
            Annuleren
          </button>
          <button
            onClick={slaOp}
            disabled={fouten.length > 0}
            className="flex-1 py-2 rounded-lg text-sm font-semibold text-white bg-brand-700 hover:bg-brand-800 disabled:opacity-40 transition"
          >
            {isNieuw ? 'Toevoegen' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
